import { useState, useEffect, useCallback } from 'react';
import styles from './NotaFiscalPedido.module.css';

const API_URL = process.env.REACT_APP_API_URL;

const FORMAS = [
  { codigo: '01', rotulo: 'Dinheiro' },
  { codigo: '17', rotulo: 'Pix' },
  { codigo: '03', rotulo: 'Cartão de crédito' },
  { codigo: '04', rotulo: 'Cartão de débito' },
  { codigo: '10', rotulo: 'Vale refeição' },
  { codigo: '11', rotulo: 'Vale alimentação' },
  { codigo: '99', rotulo: 'Outros' }
];

const ROTULO_STATUS = {
  processando: 'Processando',
  autorizada: 'Autorizada',
  rejeitada: 'Rejeitada',
  cancelada: 'Cancelada',
  erro: 'Sem resposta do provedor'
};

function cabecalhos() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function chamar(caminho, opcoes = {}) {
  const res = await fetch(`${API_URL}${caminho}`, { headers: cabecalhos(), ...opcoes });
  const dados = await res.json().catch(() => ({}));
  if (!res.ok) {
    const erro = new Error(dados.error || 'Falha na requisição');
    erro.detalhes = dados.detalhes || [];
    throw erro;
  }
  return dados;
}

// Seção "Nota fiscal" do detalhe do pedido: emitir, acompanhar, baixar e
// cancelar a NFC-e. Some quando a emissão fiscal não está ativa no
// estabelecimento (ver tela Fiscal).
function NotaFiscalPedido({ idPedido }) {
  const [info, setInfo] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState(null);
  const [forma, setForma] = useState('');
  const [cpf, setCpf] = useState('');

  const carregar = useCallback(async (atualizar = false) => {
    try {
      setInfo(await chamar(`/fiscal/pedidos/${idPedido}/nfce${atualizar ? '?atualizar=1' : ''}`));
      setErro(null);
    } catch (e) {
      setErro({ mensagem: e.message, detalhes: [] });
    }
  }, [idPedido]);

  useEffect(() => { carregar(); }, [carregar]);

  const executar = async (fn) => {
    setOcupado(true);
    setErro(null);
    try { await fn(); } catch (e) { setErro({ mensagem: e.message, detalhes: e.detalhes || [] }); } finally { setOcupado(false); }
  };

  const emitir = () => executar(async () => {
    await chamar(`/fiscal/pedidos/${idPedido}/nfce`, {
      method: 'POST',
      body: JSON.stringify({ forma_pagamento: forma || undefined, cpf: cpf || undefined })
    });
    await carregar();
  });

  const cancelar = () => {
    const justificativa = window.prompt('Justificativa do cancelamento (mínimo 15 caracteres):');
    if (!justificativa) return;
    executar(async () => {
      await chamar(`/fiscal/pedidos/${idPedido}/nfce/cancelar`, { method: 'POST', body: JSON.stringify({ justificativa }) });
      await carregar();
    });
  };

  // Sem resposta (ex.: usuário sem acesso fiscal): a seção simplesmente não aparece.
  if (!info) return null;
  if (!info.habilitado) return null;

  const nota = info.nota;
  const podeEmitir = !nota || nota.status === 'rejeitada' || nota.status === 'cancelada';
  const exigeForma = !info.forma_pagamento_reconhecida;

  return (
    <section className={styles.secao}>
      <h3 className={styles.titulo}>
        Nota fiscal (NFC-e)
        {info.ambiente === 'homologacao' && <span className={styles.selo}>homologação</span>}
      </h3>

      {info.certificado_dias_restantes !== null && info.certificado_dias_restantes !== undefined && info.certificado_dias_restantes <= 30 && (
        <p className={styles.erro}>
          {info.certificado_dias_restantes < 0
            ? 'O certificado digital venceu — não é possível emitir notas até enviar um novo em Fiscal.'
            : `O certificado digital vence em ${info.certificado_dias_restantes} dia(s). Envie um novo em Fiscal.`}
        </p>
      )}

      {nota && (
        <div className={styles.nota}>
          <span className={`${styles.status} ${styles[nota.status]}`}>{ROTULO_STATUS[nota.status] || nota.status}</span>
          {nota.numero && <span className={styles.numero}>Nº {nota.numero} · série {nota.serie}</span>}
          {nota.mensagem && nota.status !== 'autorizada' && <p className={styles.mensagem}>{nota.mensagem}</p>}
          {nota.chave && <p className={styles.chave}>Chave: {nota.chave}</p>}
          <div className={styles.links}>
            {nota.url_danfe && <a href={nota.url_danfe} target="_blank" rel="noreferrer">Ver cupom (DANFE)</a>}
            {nota.url_xml && <a href={nota.url_xml} target="_blank" rel="noreferrer">Baixar XML</a>}
          </div>
        </div>
      )}

      {podeEmitir && (
        <div className={styles.formulario}>
          {exigeForma && (
            <label className={styles.campo}>
              <span>Forma de pagamento na nota (pedido: "{info.forma_pagamento_pedido}")</span>
              <select value={forma} onChange={(e) => setForma(e.target.value)}>
                <option value="">Selecione</option>
                {FORMAS.map((f) => <option key={f.codigo} value={f.codigo}>{f.rotulo}</option>)}
              </select>
            </label>
          )}
          <label className={styles.campo}>
            <span>CPF na nota (opcional)</span>
            <input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="Somente números" inputMode="numeric" />
          </label>
          <button className={styles.btnPrimario} onClick={emitir} disabled={ocupado || (exigeForma && !forma)}>
            {ocupado ? 'Emitindo...' : nota ? 'Emitir novamente' : 'Emitir NFC-e'}
          </button>
        </div>
      )}

      {nota && !podeEmitir && (
        <div className={styles.acoes}>
          <button className={styles.btnSecundario} onClick={() => executar(() => carregar(true))} disabled={ocupado}>Atualizar status</button>
          {nota.status === 'autorizada' && (
            <button className={styles.btnPerigo} onClick={cancelar} disabled={ocupado}>Cancelar nota</button>
          )}
        </div>
      )}

      {erro && (
        <div className={styles.erro}>
          <p>{erro.mensagem}</p>
          {erro.detalhes.filter((d) => d !== 'forma_pagamento_indefinida').length > 0 && (
            <ul>{erro.detalhes.filter((d) => d !== 'forma_pagamento_indefinida').map((d) => <li key={d}>{d}</li>)}</ul>
          )}
        </div>
      )}
    </section>
  );
}

export default NotaFiscalPedido;
